/**
 * Strict browser-side contracts for the mail folder chooser. Microsoft object
 * identifiers stay opaque: the UI only compares and returns them, while every
 * displayed label and count is validated before rendering.
 */

export type MailFolderAnchor = "inbox" | "sentitems";

export interface MailFolder {
  readonly id: string;
  readonly displayName: string;
  readonly parentFolderId: string | null;
  /** The folder's place in the mailbox, already joined by the service. */
  readonly path: string;
  readonly totalItemCount: number;
  /**
   * Which always-read folder this is, or `null`. An anchor is shown as scope
   * the member already has rather than as a choice they can undo.
   */
  readonly anchor: MailFolderAnchor | null;
}

/**
 * One bounded walk of the mailbox. `truncated` is the service's own statement
 * that it hit a bound before the mailbox ran out of folders; the browser never
 * derives a total from it.
 */
export interface MailFolderPage {
  readonly folders: readonly MailFolder[];
  readonly truncated: boolean;
}

export interface MailFolderSelection {
  readonly connectionId: string;
  readonly folderId: string;
  readonly displayName: string;
  readonly parentFolderId: string | null;
  readonly totalItemCount: number;
  readonly historyStartAt: string;
  readonly selectedAt: string;
  readonly updatedAt: string;
}

export class MailFolderContractError extends Error {
  constructor() {
    super("The mail folder service returned an invalid response.");
    this.name = "MailFolderContractError";
  }
}

function invalid(): never {
  throw new MailFolderContractError();
}

function record(value: unknown): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return invalid();
  }
  return value as Record<string, unknown>;
}

function exactKeys(
  value: Record<string, unknown>,
  expected: readonly string[],
): void {
  const actual = Object.keys(value).sort();
  const allowed = [...expected].sort();
  if (
    actual.length !== allowed.length ||
    actual.some((key, index) => key !== allowed[index])
  ) {
    invalid();
  }
}

/**
 * Exact keys, plus a named set that may be absent. Additive contract fields are
 * introduced this way so a browser reads both the version that sends them and
 * the version that does not; every other unknown key stays rejected.
 */
function boundedKeys(
  value: Record<string, unknown>,
  required: readonly string[],
  optional: readonly string[],
): void {
  const allowed = new Set([...required, ...optional]);
  if (Object.keys(value).some((key) => !allowed.has(key))) invalid();
  if (required.some((key) => !(key in value))) invalid();
}

function containsControlCharacter(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

function boundedString(value: unknown, maximum = 2_048): string {
  if (
    typeof value !== "string" ||
    value.length === 0 ||
    value.length > maximum ||
    containsControlCharacter(value)
  ) {
    return invalid();
  }
  return value;
}

function opaqueIdentifier(value: unknown): string {
  const identifier = boundedString(value);
  if (/[\\/?#]/u.test(identifier)) return invalid();
  return identifier;
}

function nullableIdentifier(value: unknown): string | null {
  return value === null ? null : opaqueIdentifier(value);
}

function connectionIdentifier(value: unknown): string {
  const identifier = boundedString(value, 36).toLowerCase();
  if (
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u.test(
      identifier,
    )
  ) {
    return invalid();
  }
  return identifier;
}

function itemCount(value: unknown): number {
  if (
    typeof value !== "number" ||
    !Number.isSafeInteger(value) ||
    value < 0 ||
    value > 100_000_000
  ) {
    return invalid();
  }
  return value;
}

function timestamp(value: unknown): string {
  const raw = boundedString(value, 64);
  if (!Number.isFinite(Date.parse(raw))) return invalid();
  return raw;
}

function anchor(value: unknown): MailFolderAnchor | null {
  if (value === null) return null;
  if (value === "inbox" || value === "sentitems") return value;
  return invalid();
}

function valueArray(value: unknown): readonly unknown[] {
  const envelope = record(value);
  exactKeys(envelope, ["value"]);
  if (!Array.isArray(envelope.value)) return invalid();
  return envelope.value;
}

export function parseMailFolder(value: unknown): MailFolder {
  const folder = record(value);
  exactKeys(folder, [
    "id",
    "displayName",
    "parentFolderId",
    "path",
    "totalItemCount",
    "anchor",
  ]);
  return {
    id: opaqueIdentifier(folder.id),
    displayName: boundedString(folder.displayName, 320),
    parentFolderId: nullableIdentifier(folder.parentFolderId),
    path: boundedString(folder.path, 4_096),
    totalItemCount: itemCount(folder.totalItemCount),
    anchor: anchor(folder.anchor),
  };
}

/**
 * The folder page envelope. `truncated` is additive and optional so web and API
 * ship independently; an absent field is not a claim that the walk was complete,
 * it means no truncation was reported.
 */
export function parseMailFolderPage(value: unknown): MailFolderPage {
  const page = record(value);
  boundedKeys(page, ["value"], ["truncated"]);
  if (!Array.isArray(page.value)) return invalid();
  if (page.truncated !== undefined && typeof page.truncated !== "boolean") {
    return invalid();
  }
  return {
    folders: page.value.map(parseMailFolder),
    truncated: page.truncated === true,
  };
}

export function parseMailFolderSelection(value: unknown): MailFolderSelection {
  const selection = record(value);
  exactKeys(selection, [
    "connectionId",
    "folderId",
    "displayName",
    "parentFolderId",
    "totalItemCount",
    "historyStartAt",
    "selectedAt",
    "updatedAt",
  ]);
  return {
    connectionId: connectionIdentifier(selection.connectionId),
    folderId: opaqueIdentifier(selection.folderId),
    displayName: boundedString(selection.displayName, 320),
    parentFolderId: nullableIdentifier(selection.parentFolderId),
    totalItemCount: itemCount(selection.totalItemCount),
    historyStartAt: timestamp(selection.historyStartAt),
    selectedAt: timestamp(selection.selectedAt),
    updatedAt: timestamp(selection.updatedAt),
  };
}

export function parseMailFolderSelectionList(
  value: unknown,
): readonly MailFolderSelection[] {
  return valueArray(value).map(parseMailFolderSelection);
}
